import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import authService from '../api-authorization/AuthorizeService';
import AdminFunctions from './AdminFunctions';
import AuthFunctions from '../services/AuthService';
import Session from '../auth/Session'
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap";

const emptyEquipment = {
  id: 0,
  equipmentName: "",
  category: "",
  modelNumber: "",
  notes: "",
  isActive: true,
};

const Equipment = () => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [equipment, setEquipment] = useState([]);
    const navigate = useNavigate();

    // modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [form, setForm] = useState(emptyEquipment);
    const [equipmentSearch, setEquipmentSearch] = useState('');

    const categories = useMemo(
        () => ["Ballcart", "Bobcat", "Cut-Off Saw", "Ditchwitch", "Jaws", "Trencher", "Vehicle", "Trailer"],
        []
    );

    useEffect(() => {
        async function isAuth() {
            var _token = Session.getToken();
            var isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) {
                navigate('/login');
            }
        }
        async function fetchIsAdmin() {
            const isAdmin = await AdminFunctions.isAdminAsync();
            if (!isAdmin) {
                navigate('/accessdenied');
                return;
            }
        }

        isAuth();
        fetchIsAdmin();
    }, [navigate]);

    useEffect(() => {
        loadEquipment();
    }, []);

    const loadEquipment = async () => {
        try{
            setError("");
            setLoading(true);
            const data = await getEquipment();
            setEquipment(data || []);
        } catch (e) {
            setError(e?.message || "Failed to load equipment.")
        } finally {
            setLoading(false);
        }
    }

    const toggleModal = () => setIsModalOpen((v) => !v);

    const openAddModal = () => {
        setIsEditMode(false);
        setForm(emptyEquipment);
        setIsModalOpen(true);
    };

    const openEditModal = (row) => {
        setIsEditMode(true);
        setForm({ ...row });
        setIsModalOpen(true);
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        // basic validation
        if (!form.equipmentName?.trim()) {
            alert("Equipment Name is required.");
            return;
        }
        if (!form.category?.trim()) {
            alert("Category is required.");
            return;
        }

        const payload = {
            id: form.id ?? null,
            equipmentName: form.equipmentName?.trim(),
            category: form.category?.trim(),
            modelNumber: form.modelNumber?.trim() || null,
            notes: form.notes?.trim() || null,
            isActive: !!form.isActive
        }

        try{
            setSaving(true);
            setError("");

            if(isEditMode && form.id){
                await updateEquipment(form.id, payload);

                // optimistic update in UI
                setEquipment((prev) => prev.map((e) => (e.id === form.id ? { ...e, ...form } : e)))
            } else {
                const created = await createEquipment(payload);

                const newRow = {
                    id: created.id,
                    equipmentName: created.equipmentName,
                    category: created.category,
                    modelNumber: created.modelNumber,
                    notes: created.notes,
                    isActive: created.isActive ?? true
                };

                setEquipment((prev) => [newRow, ...prev]);
            }

            setIsModalOpen(false);
        } catch (e) {
            setError(e?.message || "Save failed.")
        } finally {
            setSaving(false);
        }
    };

    const getEquipment = async () => {
        var token = Session.getToken();
        const response = await fetch(`api/Equipment/GetAll`, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        });

        if(!response.ok){
            const text = await response.text();
            throw new Error(text || "Failed to load equipment.");
        }

        return await response.json();
    }

    const createEquipment = async (payload) => {
        var token = Session.getToken();
        const response = await fetch(`api/Equipment`, {
            method: "POST",
            headers: !token ? { "Content-Type": "application/json" } : { 'Authorization': `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });

        if(!response.ok){
            const text = await response.text();
            throw new Error(text || "Failed to create equipment.");
        }
        
        return await response.json();
    }

    const updateEquipment = async (id, payload) => {
        var token = Session.getToken();
        const response = await fetch(`api/Equipment/${id}`, {
            method: "PUT",
            headers: !token ? { "Content-Type": "application/json" } : { 'Authorization': `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });

        if(!response.ok){
            const text = await response.text();
            throw new Error(text || "Failed to update equipment.");
        }
    }

    const filteredEquipment = useMemo(() => {
        const term = equipmentSearch.trim().toLowerCase();
        if (!term) return equipment;

        return equipment.filter((e) => {
            return (
            (e.equipmentName || '').toLowerCase().includes(term) ||
            (e.category || '').toLowerCase().includes(term) ||
            (e.notes || '').toLowerCase().includes(term) ||
            (e.modelNumber || '').toLowerCase().includes(term)
            );
        });
    }, [equipment, equipmentSearch]);

    return (
        <div>
            {error ? (
                <Row>
                    <Col>
                        <div className="alert alert-danger mb-0">{error}</div>
                    </Col>
                </Row>
            ) : null}
            
            <Container className="container-main">
                {/* Header */}
                <Row className="align-items-center g-3">
                    <Col>
                        <h3>Equipment</h3>
                    </Col>

                    <Col xs="12" md="auto">
                        <div className="d-flex gap-3 w-100">
                            <Button
                                color="primary"
                                className="flex-fill flex-md-grow-0 text-nowrap"
                                onClick={openAddModal}
                            >
                                + Add
                            </Button>
                        </div>
                    </Col>
                </Row>

                {loading ? (
                <div
                    style={{
                    marginTop: "2rem",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    }}
                >
                    <Spinner />
                </div>
                ) : (
                <>
                    {/* Search */}
                    <Row className="row-20" style={{ marginTop: 10 }}>
                        <Col md="6">
                            <Input
                            type="text"
                            value={equipmentSearch}
                            onChange={(e) => setEquipmentSearch(e.target.value)}
                            placeholder="Search equipment..."
                            />
                        </Col>
        
                        <Col md="6" className="d-flex justify-content-md-end align-items-center">
                            <div className="text-muted" style={{ fontSize: 14, alignSelf: "end" }}>
                            Showing {filteredEquipment.length} of {equipment.length}
                            </div>
                        </Col>
                    </Row>

                    {/* Equipment table */}
                    <Row className="row-20">
                        <Col>
                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                    <th>Equipment Name</th>
                                    <th>Category</th>
                                    <th>Model #</th>
                                    <th>Notes</th>
                                    <th>Active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEquipment.map((row) => (
                                    <tr
                                        key={row.id}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => openEditModal(row)}
                                    >
                                        <td>{row.equipmentName}</td>
                                        <td>{row.category}</td>
                                        <td>{row.modelNumber}</td>
                                        <td style={{ maxWidth: 380 }}>{row.notes}</td>
                                        <td>
                                        <Input
                                            type="checkbox"
                                            disabled
                                            checked={row.isActive}
                                            name="active"
                                        />
                                        </td>
                                    </tr>
                                    ))}
                                    {filteredEquipment.length === 0 && (
                                    <tr>
                                        <td colSpan={6} style={{ textAlign: "center" }}>
                                        No equipment found.
                                        </td>
                                    </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </>
                )}

                {/* Add/Edit Modal */}
                <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
                    <ModalHeader toggle={toggleModal}>
                        {isEditMode ? "Edit Equipment" : "Add Equipment"}
                    </ModalHeader>

                    <ModalBody>
                        <Form>
                            <Row>
                                <Col md={6}>
                                <FormGroup>
                                    <Label for="equipmentName">Equipment Name</Label>
                                    <Input
                                    id="equipmentName"
                                    type="text"
                                    value={form.equipmentName}
                                    onChange={(e) =>
                                        handleChange("equipmentName", e.target.value)
                                    }
                                    placeholder="e.g. Bobcat S650"
                                    />
                                </FormGroup>
                                </Col>

                                <Col md={6}>
                                <FormGroup>
                                    <Label for="category">Category</Label>
                                    <Input
                                    id="category"
                                    type="select"
                                    value={form.category}
                                    onChange={(e) => handleChange("category", e.target.value)}
                                    >
                                    <option value="">-- Select --</option>
                                    {categories.map((c) => (
                                        <option key={c} value={c}>
                                        {c}
                                        </option>
                                    ))}
                                    </Input>
                                </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                <FormGroup>
                                    <Label for="modelNumber">Model #</Label>
                                    <Input
                                    id="modelNumber"
                                    type="text"
                                    value={form.modelNumber}
                                    onChange={(e) =>
                                        handleChange("modelNumber", e.target.value)
                                    }
                                    placeholder="e.g. RT60-0021"
                                    />
                                </FormGroup>
                                </Col>

                                <Col md={6} className="d-flex align-items-center">
                                <div className="d-flex gap-4">
                                    <FormGroup check>
                                        <Label check>
                                            <Input
                                            type="checkbox"
                                            checked={!!form.isActive}
                                            onChange={(e) => handleChange("isActive", e.target.checked)}
                                            />{" "}
                                            Active
                                        </Label>
                                    </FormGroup>
                                </div>
                                </Col>
                            </Row>

                            <FormGroup>
                                <Label for="notes">Notes</Label>
                                <Input
                                id="notes"
                                type="textarea"
                                rows={4}
                                value={form.notes}
                                onChange={(e) => handleChange("notes", e.target.value)}
                                placeholder="Any notes about condition, location, maintenance, etc."
                                />
                            </FormGroup>
                        </Form>
                    </ModalBody>

                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal} disabled={saving}>
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleSave} disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default Equipment;